(function () {
  if (customElements.get('lkf-venue-map')) return;

  var INITIAL_SCALE = 1.45;
  var MIN_SCALE = 1;
  var MAX_SCALE = 3;
  var ZOOM_STEP = 0.25;
  var PAN_STEP = 48;
  var DESKTOP_QUERY = '(min-width: 960px)';

  function clampNumber(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function fillCount(template, shown, total) {
    return String(template || '')
      .split('__COUNT__')
      .join(String(shown))
      .split('__TOTAL__')
      .join(String(total));
  }

  class LkfVenueMap extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this._abort = new AbortController();
      this._signal = this._abort.signal;
      this._restoreFocus = false;
      this._lastFocus = null;
      this._pointers = new Map();
      this._moved = false;
      this._suppressClick = false;
      this._lastTap = 0;
      this._desktop = null;
      this._mobileReady = false;

      this._viewport = this.querySelector('[data-viewport]');
      this._canvas = this.querySelector('[data-canvas]');
      this._filters = this.querySelector('[data-filters]');
      this._search = this.querySelector('[data-search]');
      this._count = this.querySelector('[data-match-count]');
      this._noResults = this.querySelector('[data-no-results]');
      this._dialog = this.querySelector('[data-dialog]');
      this._dialogBody = this.querySelector('[data-dialog-body]');
      this._zoom = this.querySelector('[data-zoom]');
      this._pins = this.querySelector('[data-pins]');

      if (!this._viewport || !this._canvas || !this._dialog || !this._dialogBody) return;

      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.bind();
      this.applyFilters();
      this.warnIssues();
      this.syncLayout(true);
      this._resizeObserver = new ResizeObserver(this.onResize.bind(this));
      this._resizeObserver.observe(this._viewport);
    }

    disconnectedCallback() {
      var dialog = this._dialog;
      this._restoreFocus = false;
      if (this._abort) this._abort.abort();
      this._abort = null;
      this._signal = null;
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this._resizeObserver = null;
      if (dialog && dialog.open) dialog.close();
      if (dialog) {
        var body = dialog.querySelector('[data-dialog-body]');
        if (body) body.replaceChildren();
      }
    }

    bind() {
      var signal = this._signal;
      var root = this;

      if (this._filters) {
        this._filters.addEventListener('click', function (event) {
          var chip = event.target.closest('[data-category]');
          if (!chip || !root._filters.contains(chip)) return;
          root.selectCategory(chip);
        }, { signal: signal });
      }

      if (this._search) {
        this._search.addEventListener('input', function () {
          root.applyFilters();
        }, { signal: signal });
      }

      this.addEventListener('click', function (event) {
        if (!root._suppressClick) return;
        root._suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true, signal: signal });

      if (this._pins) {
        this._pins.addEventListener('click', function (event) {
          var pin = event.target.closest('[data-pin]');
          if (!pin) return;
          root.openVenue(pin.dataset.venueId, pin);
        }, { signal: signal });
      }

      this._viewport.addEventListener('pointerdown', this.onPointerDown.bind(this), { signal: signal });
      this._viewport.addEventListener('pointermove', this.onPointerMove.bind(this), { signal: signal });
      this._viewport.addEventListener('pointerup', this.onPointerUp.bind(this), { signal: signal });
      this._viewport.addEventListener('pointercancel', this.onPointerUp.bind(this), { signal: signal });
      this._viewport.addEventListener('keydown', this.onKeyDown.bind(this), { signal: signal });

      this._dialog.addEventListener('click', function (event) {
        if (event.target === root._dialog) root._dialog.close();
      }, { signal: signal });
      this._dialog.addEventListener('close', function () {
        var trigger = root._lastFocus;
        root.clearDialogFit();
        if (root._dialogBody) root._dialogBody.replaceChildren();
        if (!root._restoreFocus || !trigger || !trigger.isConnected) return;
        trigger.focus();
      }, { signal: signal });

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', function () {
          if (root._dialog && root._dialog.open) root.fitDialog();
        }, { signal: signal });
        window.visualViewport.addEventListener('scroll', function () {
          if (root._dialog && root._dialog.open) root.fitDialog();
        }, { signal: signal });
      }

      var closeButton = this._dialog.querySelector('[data-close]');
      if (closeButton) {
        closeButton.addEventListener('click', function () {
          root._dialog.close();
        }, { signal: signal });
      }

      if (this._zoom) {
        this._zoom.addEventListener('click', function (event) {
          var control = event.target.closest('[data-zoom-action]');
          if (!control || root.isDesktop()) return;
          var action = control.dataset.zoomAction;
          if (action === 'in') root.zoomAtCenter(root.scale + ZOOM_STEP);
          else if (action === 'out') root.zoomAtCenter(root.scale - ZOOM_STEP);
          else if (action === 'reset') root.syncLayout(true);
        }, { signal: signal });
      }
    }

    selectCategory(chip) {
      var chips = this._filters.querySelectorAll('[data-category]');
      var wasPressed = chip.getAttribute('aria-pressed') === 'true';
      var next = chip;
      if (chip.dataset.category !== 'all' && wasPressed) {
        next = this._filters.querySelector('[data-category="all"]') || chip;
      }
      chips.forEach(function (item) {
        item.setAttribute('aria-pressed', item === next ? 'true' : 'false');
      });
      this.applyFilters();
    }

    activeCategory() {
      var chip = this._filters && this._filters.querySelector('[data-category][aria-pressed="true"]');
      return chip ? chip.dataset.category : 'all';
    }

    applyFilters() {
      var category = this.activeCategory();
      var query = this._search ? this._search.value.trim().toLowerCase() : '';
      var pins = this._pins ? Array.prototype.slice.call(this._pins.querySelectorAll('[data-pin]')) : [];
      var filtering = category !== 'all' || query.length > 0;
      var shown = 0;

      pins.forEach(function (pin) {
        var name = (pin.dataset.name || '').toLowerCase();
        var address = (pin.dataset.address || '').toLowerCase();
        var categories = (pin.dataset.categories || pin.dataset.category || '').toLowerCase().split(/\s+/);
        var categoryOk = category === 'all' || categories.indexOf(category) !== -1;
        var searchOk = !query || name.indexOf(query) !== -1 || address.indexOf(query) !== -1;
        var match = categoryOk && searchOk;
        if (match) shown += 1;
        pin.classList.toggle('is-dimmed', filtering && !match);
      });

      if (this._count) {
        var template = this._count.dataset.countTemplate || '__COUNT__ / __TOTAL__';
        this._count.textContent = fillCount(template, shown, pins.length);
      }
      if (this._noResults) this._noResults.hidden = !(pins.length > 0 && shown === 0);
    }

    openVenue(id, trigger) {
      if (!id) return;
      var escaped = window.CSS && CSS.escape ? CSS.escape(String(id)) : String(id).replace(/"/g, '');
      var template = this.querySelector('template[data-venue-template="' + escaped + '"]');
      if (!template) return;
      this._lastFocus = trigger || document.activeElement;
      this._restoreFocus = true;
      this._dialogBody.replaceChildren(template.content.cloneNode(true));
      var title = this._dialogBody.querySelector('[data-venue-title]');
      if (title && title.id) this._dialog.setAttribute('aria-labelledby', title.id);
      if (!this._dialog.open) this._dialog.showModal();
      this.fitDialog();
      var sheet = this._dialog.querySelector('[data-sheet]');
      if (sheet) sheet.scrollTop = 0;
      var closeButton = this._dialog.querySelector('[data-close]');
      if (closeButton) closeButton.focus({ preventScroll: true });
    }

    fitDialog() {
      var dialog = this._dialog;
      if (!dialog || !dialog.open) return;
      if (this.isDesktop() || !window.visualViewport) {
        this.clearDialogFit();
        return;
      }
      var viewport = window.visualViewport;
      var top = viewport.offsetTop + 'px';
      var height = viewport.height + 'px';
      if (dialog.style.top !== top) dialog.style.top = top;
      if (dialog.style.height !== height) dialog.style.height = height;
      if (dialog.style.maxHeight !== height) dialog.style.maxHeight = height;
    }

    clearDialogFit() {
      var dialog = this._dialog;
      if (!dialog) return;
      dialog.style.top = '';
      dialog.style.height = '';
      dialog.style.maxHeight = '';
    }

    isDesktop() {
      return window.matchMedia(DESKTOP_QUERY).matches;
    }

    onResize() {
      var desktop = this.isDesktop();
      var crossed = this._desktop !== null && desktop !== this._desktop;
      this._desktop = desktop;
      this.syncLayout(crossed);
      if (this._dialog && this._dialog.open) this.fitDialog();
    }

    syncLayout(resetMobile) {
      var desktop = this.isDesktop();
      this._desktop = desktop;
      if (this._zoom) this._zoom.hidden = desktop;
      if (this._viewport) this._viewport.tabIndex = desktop ? -1 : 0;
      if (desktop) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this._mobileReady = false;
        this.renderTransform();
        return;
      }
      if (resetMobile || !this._mobileReady) {
        if (this.center(INITIAL_SCALE)) this._mobileReady = true;
      } else {
        this.clamp();
      }
      this.renderTransform();
      this.updateZoomState();
    }

    center(scale) {
      var width = this._viewport.clientWidth;
      var height = this._viewport.clientHeight;
      if (!width || !height) return false;
      this.scale = clampNumber(scale, MIN_SCALE, MAX_SCALE);
      this.tx = (width - width * this.scale) / 2;
      this.ty = (height - height * this.scale) / 2;
      this.clamp();
      return true;
    }

    clamp() {
      var width = this._viewport.clientWidth;
      var height = this._viewport.clientHeight;
      var mapWidth = width * this.scale;
      var mapHeight = height * this.scale;
      var minX = Math.min(0, width - mapWidth);
      var minY = Math.min(0, height - mapHeight);
      this.tx = clampNumber(this.tx, minX, 0);
      this.ty = clampNumber(this.ty, minY, 0);
      this.updateZoomState();
    }

    renderTransform() {
      if (!this._canvas) return;
      if (this.isDesktop()) {
        this._canvas.style.transform = '';
        return;
      }
      this._canvas.style.transform = 'translate(' + this.tx + 'px, ' + this.ty + 'px) scale(' + this.scale + ')';
    }

    updateZoomState() {
      if (!this._zoom) return;
      var zoomIn = this._zoom.querySelector('[data-zoom-action="in"]');
      var zoomOut = this._zoom.querySelector('[data-zoom-action="out"]');
      if (zoomIn) zoomIn.setAttribute('aria-disabled', this.scale >= MAX_SCALE - 0.001 ? 'true' : 'false');
      if (zoomOut) zoomOut.setAttribute('aria-disabled', this.scale <= MIN_SCALE + 0.001 ? 'true' : 'false');
    }

    zoomAt(clientX, clientY, nextScale) {
      var rect = this._viewport.getBoundingClientRect();
      var pointX = clientX - rect.left;
      var pointY = clientY - rect.top;
      var scale = clampNumber(nextScale, MIN_SCALE, MAX_SCALE);
      var ratio = scale / this.scale;
      this.tx = pointX - ratio * (pointX - this.tx);
      this.ty = pointY - ratio * (pointY - this.ty);
      this.scale = scale;
      this.clamp();
      this.renderTransform();
    }

    zoomAtCenter(nextScale) {
      var rect = this._viewport.getBoundingClientRect();
      this._canvas.classList.remove('is-gesturing');
      this.zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, nextScale);
    }

    onPointerDown(event) {
      if (this.isDesktop() || event.button > 0) return;
      this._pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      try {
        this._viewport.setPointerCapture(event.pointerId);
      } catch (error) {
        /* Pointer capture is unavailable for some synthetic or cancelled pointers. */
      }
      this._canvas.classList.add('is-gesturing');
      if (this._pointers.size === 1) {
        this._panOrigin = { x: event.clientX, y: event.clientY, tx: this.tx, ty: this.ty };
        this._moved = false;
      }
    }

    onPointerMove(event) {
      if (!this._pointers.has(event.pointerId) || this.isDesktop()) return;
      this._pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this._pointers.size >= 2) {
        this.pinch();
        this._moved = true;
        return;
      }
      if (!this._panOrigin) return;
      var dx = event.clientX - this._panOrigin.x;
      var dy = event.clientY - this._panOrigin.y;
      if (Math.hypot(dx, dy) > 8) this._moved = true;
      this.tx = this._panOrigin.tx + dx;
      this.ty = this._panOrigin.ty + dy;
      this.clamp();
      this.renderTransform();
    }

    pinch() {
      var points = Array.from(this._pointers.values());
      if (points.length < 2) return;
      var distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      var midX = (points[0].x + points[1].x) / 2;
      var midY = (points[0].y + points[1].y) / 2;
      if (!this._pinch) {
        this._pinch = { distance: distance || 1, scale: this.scale };
      }
      var next = this._pinch.scale * (distance / this._pinch.distance);
      this.zoomAt(midX, midY, next);
    }

    onPointerUp(event) {
      var wasPinch = this._pointers.size >= 2;
      this._pointers.delete(event.pointerId);
      if (this._pointers.size < 2) this._pinch = null;
      if (this._pointers.size === 1) {
        var remaining = Array.from(this._pointers.values())[0];
        this._panOrigin = { x: remaining.x, y: remaining.y, tx: this.tx, ty: this.ty };
        return;
      }
      if (this._pointers.size > 0) return;
      this._canvas.classList.remove('is-gesturing');
      if (this._moved) this._suppressClick = true;
      else if (event.type !== 'pointercancel' && !wasPinch && !event.target.closest('[data-pin]')) this.doubleTap(event);
      this._moved = false;
      this._panOrigin = null;
    }

    doubleTap(event) {
      var now = Date.now();
      if (now - this._lastTap < 300) {
        this._canvas.classList.remove('is-gesturing');
        this.zoomAt(event.clientX, event.clientY, this.scale * 1.4);
        this._lastTap = 0;
        return;
      }
      this._lastTap = now;
    }

    onKeyDown(event) {
      if (this.isDesktop() || event.target !== this._viewport) return;
      var handled = true;
      if (event.key === 'ArrowLeft') this.tx += PAN_STEP;
      else if (event.key === 'ArrowRight') this.tx -= PAN_STEP;
      else if (event.key === 'ArrowUp') this.ty += PAN_STEP;
      else if (event.key === 'ArrowDown') this.ty -= PAN_STEP;
      else if (event.key === '+' || event.key === '=') this.zoomAtCenter(this.scale + ZOOM_STEP);
      else if (event.key === '-' || event.key === '_') this.zoomAtCenter(this.scale - ZOOM_STEP);
      else if (event.key === '0') this.syncLayout(true);
      else handled = false;
      if (!handled) return;
      event.preventDefault();
      this.clamp();
      this.renderTransform();
    }

    warnIssues() {
      var node = this.querySelector('[data-lkf-issues]');
      if (!node) return;
      var issues;
      try {
        issues = JSON.parse(node.textContent);
      } catch (error) {
        return;
      }
      if (issues.coordinates && issues.coordinates.length) {
        console.warn('[lkf-venue-map] Skipped venues with missing or out-of-range coordinates:', issues.coordinates.join(', '));
      }
      if (issues.categories && issues.categories.length) {
        console.warn('[lkf-venue-map] Venues with an unknown category:', issues.categories.join(', '));
      }
      if (issues.urls && issues.urls.length) {
        console.warn('[lkf-venue-map] Omitted malformed URLs:', issues.urls.join(', '));
      }
    }
  }

  customElements.define('lkf-venue-map', LkfVenueMap);
})();
